import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { PageHeader } from './PageHeader';
import './ExpertListPage.css';

interface ChatExpert {
  id: string;
  name: string;
  role: string;
  photo_url: string | null;
  is_online: boolean;
  rating: number;
  reviews_count: number;
}

interface ChatItem {
  chat_id: string;
  expert: ChatExpert;
  last_message: string | null;
  last_message_sender: 'user' | 'expert' | null;
  last_message_at: string | null;
}

const formatTime = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
};

interface ChatsListPageProps {
  /** Встроенный режим (как подраздел «Мои чаты» внутри «Экспертов») — без своей шапки. */
  embedded?: boolean;
}

export const ChatsListPage: React.FC<ChatsListPageProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.getExpertChats()
      .then((res: { data?: ChatItem[] }) => setChats(res.data ?? []))
      .catch((e: unknown) => console.error('Failed to load chats', e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className={embedded ? '' : 'cbt-experts-page'}>
      {!embedded && <PageHeader title="Мои чаты" />}

      {loading && <div className="cbt-loading">Загружаем чаты…</div>}

      {!loading && chats.length === 0 && (
        <div className="cbt-chats-empty">
          <div className="cbt-chats-empty-emoji">💬</div>
          <p className="cbt-chats-empty-text">У вас пока нет чатов с экспертами.</p>
          <button className="cbt-chats-empty-btn" onClick={() => navigate('/experts')}>
            Выбрать эксперта
          </button>
        </div>
      )}

      <div className="cbt-experts-list">
        {chats.map((c) => (
          <button
            key={c.chat_id}
            className="cbt-expert-card"
            onClick={() => navigate(`/experts/${c.expert.id}/chat`)}
          >
            <div className="cbt-expert-photo">
              {c.expert.photo_url ? (
                <img src={c.expert.photo_url} alt={c.expert.name} />
              ) : (
                <div className="cbt-expert-photo-placeholder">{c.expert.name.charAt(0)}</div>
              )}
              <span className={`cbt-expert-online-dot ${c.expert.is_online ? 'cbt-online' : 'cbt-offline'}`} />
            </div>

            <div className="cbt-expert-body">
              <div className="cbt-expert-row-top">
                <div className="cbt-expert-name">{c.expert.name}</div>
                <div className="cbt-chat-time">{formatTime(c.last_message_at)}</div>
              </div>
              <div className="cbt-expert-role">{c.expert.role}</div>
              <div className="cbt-chat-last">
                {c.last_message_sender === 'user' && <span className="cbt-chat-you">Вы: </span>}
                {c.last_message ?? ''}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
